import * as S from './TeamPage.styles';
import gql from 'graphql-tag';
import React, {FC} from 'react';
import {Loader} from 'semantic-ui-react';
import {teamPage} from '../util/urls';

import ExpandableProjectList from '../components/ExpandableProjectList';
import ProjectsTableTeam from '../components/ProjectsTableTeam';
import PageWithSidebar from '../components/PageWithSidebar';
import RunsTable, {
  runsTableFragment,
  RunsTableRun,
} from '../components/RunsTable';
import RoutedTab from '../components//RoutedTab';
import TeamSidebar from '../components/TeamSidebar';
import {Graph} from '../types/graphql';
import {Project} from '../graphql/allProjectsQuery';
import {EntityRunsQueryResult, UserAccountType} from '../generated/graphql';
import DeferredQueryData from '../containers/DeferredQueryData';
import makeComp from '../util/profiler';
import {useHistory} from 'react-router-dom';

export const teamPageFragment = gql`
  fragment TeamPageFragment on Entity {
    id
    name
    memberCount
    photoUrl
    available
    readOnly
    storageBytes
    userStats(from: $userStatsStartDate, first: 10) {
      edges {
        node {
          user {
            id
            username
            photoUrl
            accountType
          }
          runCount
        }
      }
    }
    projects(order: "lastActive") {
      edges {
        node {
          id
          name
          entityName
          description
          totalRuns
          totalUsers
          lastActive
          totalRunTime
        }
      }
    }
    claimingEntity {
      id
      name
    }
  }
`;

export const ENTITY_RUNS_QUERY = gql`
  query EntityRuns($entityName: String!) {
    entity(name: $entityName) {
      id
      runs(first: 100) {
        edges {
          node {
            ...RunsTableFragment
          }
        }
      }
    }
  }
  ${runsTableFragment}
`;

interface EntityUser {
  user: {
    id: string;
    username: string;
    photoUrl: string;
    accountType?: UserAccountType;
  };
  runCount: number;
}

interface ClaimingEntity {
  id: string;
  name: string;
}

export interface TeamPageData {
  id: string;
  name: string;
  memberCount: number;
  projects: Graph<Project>;
  runs: Graph<RunsTableRun>;
  userStats: Graph<EntityUser>;
  photoUrl: string;
  storageBytes: number;
  available: boolean;
  readOnly: boolean;
  claimingEntity: ClaimingEntity;
}

interface TeamPageProps {
  data?: TeamPageData;
  entityRunsQuery: EntityRunsQueryResult;
  tab?: string;
  useHistory?: any;
  filePath?: string;
}

const TeamPage: FC<TeamPageProps> = makeComp(
  ({data, entityRunsQuery, tab}) => {
    const history = useHistory();
    if (!data) {
      return <Loader />;
    }

    return (
      <S.Wrapper>
        <PageWithSidebar>
          <TeamSidebar data={data} />
          <div className="main-page">
            <RoutedTab
              history={history}
              baseUrl={teamPage(data.name)}
              tabSlugs={['overview', 'projects']}
              activeTabSlug={tab}
              panes={[
                {
                  menuItem: 'Overview',
                  render: () => (
                    <div>
                      <ExpandableProjectList
                        projects={data.projects}
                        entityName={data.name}
                        readOnly={data.readOnly}
                      />
                      <DeferredQueryData query={entityRunsQuery}>
                        {runsData => {
                          const runs = runsData?.entity?.runs ?? {edges: []};
                          return (
                            <RunsTable
                              name="Runs"
                              data={runs}
                              showUserColumn={true}
                            />
                          );
                        }}
                      </DeferredQueryData>
                    </div>
                  ),
                },
                {
                  menuItem: 'Projects',
                  render: () => (
                    <div>
                      <ProjectsTableTeam
                        entityName={data.name}
                        readOnly={data.readOnly}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </PageWithSidebar>
      </S.Wrapper>
    );
  },
  {id: 'TeamPage', memo: true}
);

export default TeamPage;
